import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../../../context/AlertContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import {
  closeSession,
  generateApiToken,
  type Session,
  type ApiToken,
} from "../../../../../store/admin/sessionsStore";
import { useSessionsList } from "../../../../../features/sessions/queries";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { getError } from "../../../../../utils/getError";
import { getThemeClasses, getCustomSelectStyles } from "../../../shared";

// Components
import SessionsHeader from "./SessionsHeader";
import SessionsTable from "./SessionsTable";
import SessionCard from "./SessionCard";
import GenerateTokenModal from "./modals/GenerateTokenModal";
import PaginationControls from "../../../../../components/base/PaginationControls";

export default function SessionsSection() {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation("adminSections");
  const { showAlert } = useAlert();
  const disableCRUD = useDisableCRUD();

  const notifySelectCompany = () =>
    showAlert({
      title: t("sessions.title"),
      text: t("common:selectCompanyTooltip", {
        defaultValue: "Selecciona una compañía para continuar",
      }),
      icon: "warning",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });

  // Theme
  const themeClasses = getThemeClasses(isDarkMode);
  const customSelectStyles = getCustomSelectStyles(isDarkMode);

  // Modals
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<ApiToken | null>(null);
  const [generatingToken, setGeneratingToken] = useState(false);

  // Logout loading state (tracks which session is being logged out)
  const [loggingOutSessionId, setLoggingOutSessionId] = useState<string | null>(
    null,
  );

  // Refresh button state
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [refreshCountdown, setRefreshCountdown] = useState<number>(0);

  // View
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");

  // Filters
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // First render ref
  const isFirstRender = useRef(true);

  const filterParams = useMemo(() => {
    const params: {
      page: number;
      limit: number;
      active?: string;
      platform?: string;
      search?: string;
    } = {
      page: currentPage,
      limit: itemsPerPage,
    };
    if (selectedStatus !== "all") params.active = selectedStatus;
    if (selectedPlatform !== "all") params.platform = selectedPlatform;
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [currentPage, itemsPerPage, selectedStatus, selectedPlatform, debouncedSearch]);

  const sessionsQuery = useSessionsList(filterParams);
  const sessions = sessionsQuery.data?.data ?? [];
  const meta = sessionsQuery.data?.meta;
  const loading = sessionsQuery.isLoading;
  const error = sessionsQuery.error ? getError(sessionsQuery.error) : null;
  const totalPages = meta?.totalPages || 1;

  const queryClient = useQueryClient();
  const invalidateSessions = () =>
    queryClient.invalidateQueries({ queryKey: ["sessions"] });

  // Countdown para el botón de refresh
  useEffect(() => {
    if (refreshCountdown > 0) {
      const timer = setTimeout(() => {
        setRefreshCountdown(refreshCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [refreshCountdown]);

  // Reset a la primera página cuando cambian los filtros
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedStatus, selectedPlatform, debouncedSearch]);

  // Handlers
  const handleRefresh = async () => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime;

    // Si han pasado menos de 30 segundos, no permitir refrescar
    if (timeSinceLastRefresh < 30000 && refreshCountdown > 0) {
      return;
    }

    setRefreshing(true);
    await sessionsQuery.refetch();
    setLastRefreshTime(now);
    setRefreshCountdown(30); // Iniciar countdown de 30 segundos
    setRefreshing(false);
  };

  const handleViewSession = (session: Session) => {
    // Por ahora solo mostramos un alert, ya que el modal de detalles está deshabilitado
    showAlert({
      title: t("sessions.sessionDetails"),
      text: `${session.userName} - ${session.userEmail}`,
      icon: "info",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  };

  const handleLogoutSession = (session: Session) => {
    if (disableCRUD) {
      notifySelectCompany();
      return;
    }
    showAlert({
      title: t("sessions.confirmLogout"),
      text: t("sessions.confirmLogoutMessage", { user: session.userName }),
      icon: "warning",
      confirmButtonText: t("sessions.actions.logout"),
      confirmButtonColor: "#ef4444",
      showCancelButton: true,
      cancelButtonText: t("sessions.actions.cancel"),
      onConfirm: async () => {
        setLoggingOutSessionId(session.id);
        await closeSession(session.id, showAlert, t, () => {
          invalidateSessions();
        });
        setLoggingOutSessionId(null);
      },
    });
  };

  const handleGenerateToken = async () => {
    if (generatingToken) return;
    if (disableCRUD) {
      notifySelectCompany();
      return;
    }

    setGeneratingToken(true);
    const token = await generateApiToken(
      { name: "API Token", scopes: ["admin"], expiresInDays: 0 },
      t,
      showAlert,
    );
    setGeneratingToken(false);

    if (token) {
      setGeneratedToken(token);
      setShowTokenModal(true);
    }
  };

  const handleCloseTokenModal = () => {
    setShowTokenModal(false);
    setGeneratedToken(null);
  };

  return (
    <div className="h-full flex flex-col">
      <SessionsHeader
        isDarkMode={isDarkMode}
        themeClasses={themeClasses}
        customSelectStyles={customSelectStyles}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedPlatform={selectedPlatform}
        setSelectedPlatform={setSelectedPlatform}
        search={search}
        setSearch={setSearch}
        onGenerateToken={handleGenerateToken}
        isGeneratingToken={generatingToken}
        disableCRUD={disableCRUD}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshCountdown={refreshCountdown}
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="flex flex-col items-center">
              <i className="ri-loader-4-line animate-spin text-4xl text-primary-500 mb-3"></i>
              <p className={`${themeClasses.textSecondary}`}>
                {t("sessions.loading")}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-16">
            <div className="flex flex-col items-center">
              <i className="ri-error-warning-line text-4xl text-red-500 mb-3"></i>
              <p className="text-red-500">{error}</p>
            </div>
          </div>
        ) : selectedView === "grid" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {sessions.length > 0 ? (
                sessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    themeClasses={themeClasses}
                    onView={() => handleViewSession(session)}
                    onLogout={() => handleLogoutSession(session)}
                    isLoggingOut={loggingOutSessionId === session.id}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center w-full col-span-full py-16">
                  <i
                    className={`ri-computer-line text-5xl ${themeClasses.textSecondary} mb-4`}
                  ></i>
                  <h3
                    className={`text-lg font-medium ${themeClasses.text} mb-2`}
                  >
                    {t("sessions.noSessions")}
                  </h3>
                  <p className={`${themeClasses.textSecondary} text-sm`}>
                    {t("sessions.noSessionsDescription")}
                  </p>
                </div>
              )}
            </div>

            {/* Pagination Grid */}
            {sessions.length > 0 && totalPages > 1 && (
              <div className="mt-6">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <SessionsTable
              themeClasses={themeClasses}
              sessions={sessions}
              loading={loading}
              error={error}
              onViewSession={handleViewSession}
              onLogoutSession={handleLogoutSession}
            />

            {/* Pagination List */}
            {sessions.length > 0 && totalPages > 1 && (
              <div className="py-6">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Token Modal */}
      {showTokenModal && generatedToken && (
        <GenerateTokenModal
          themeClasses={themeClasses}
          token={generatedToken}
          onClose={handleCloseTokenModal}
        />
      )}
    </div>
  );
}
